<template>
  <section v-if="isEnabled" class="bet-builder">
    <!-- Collapsed -->
    <div
      v-if="phase === 'idle'"
      class="bet-builder-card bet-builder-card--idle"
    >
      <div class="bet-builder-intro">
        <span class="bet-builder-eyebrow">{{ $t('betBuilder.badge') }}</span>
        <h2 class="bet-builder-heading">
          {{ $t('betBuilder.title') }}
        </h2>
        <p class="bet-builder-lead">
          {{ $t('betBuilder.subtitle') }}
        </p>
      </div>
      <button
        type="button"
        class="bet-builder-primary bet-builder-primary--compact"
        @click="openPanel"
      >
        {{ $t('betBuilder.start') }}
      </button>
    </div>

    <!-- Expanded -->
    <div
      v-else
      class="bet-builder-card bet-builder-card--open"
    >
      <div class="bet-builder-bar">
        <div class="bet-builder-bar-text">
          <h2 class="bet-builder-bar-title">
            {{ $t('betBuilder.title') }}
          </h2>
          <span
            v-if="sessionAddedCount > 0"
            class="bet-builder-session-count"
          >
            {{ $t('betBuilder.addedCount', { count: sessionAddedCount }) }}
          </span>
        </div>
        <button
          v-if="phase !== 'scanning'"
          type="button"
          class="bet-builder-dismiss"
          :aria-label="$t('common.close')"
          @click="closePanel"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <div class="bet-builder-body">
        <!-- Config -->
        <div v-if="phase === 'config'" class="bet-builder-config">
          <p class="bet-builder-body-lead">
            {{ $t('betBuilder.configLead') }}
          </p>

          <div class="bet-builder-fields">
            <label class="bet-builder-field">
              <span class="bet-builder-label">{{ $t('betBuilder.targetWin') }}</span>
              <div class="bet-builder-control">
                <input
                  v-model.number="localTargetWin"
                  type="number"
                  min="1"
                  step="1"
                  inputmode="numeric"
                  class="bet-builder-input"
                >
                <div class="bet-builder-step-buttons">
                  <button
                    type="button"
                    class="bet-builder-step-btn"
                    :aria-label="$t('betBuilder.increaseTarget')"
                    @click="adjustTarget(100)"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    class="bet-builder-step-btn"
                    :aria-label="$t('betBuilder.decreaseTarget')"
                    @click="adjustTarget(-100)"
                  >
                    −
                  </button>
                </div>
              </div>
            </label>

            <label class="bet-builder-field">
              <span class="bet-builder-label">{{ $t('betBuilder.stake') }}</span>
              <div class="bet-builder-control">
                <input
                  v-model.number="localStake"
                  type="number"
                  :min="betMin"
                  :max="betMax"
                  :step="stakeStep"
                  inputmode="decimal"
                  class="bet-builder-input"
                >
                <div class="bet-builder-step-buttons">
                  <button
                    type="button"
                    class="bet-builder-step-btn"
                    :aria-label="$t('betBuilder.increaseStake')"
                    @click="adjustStake(stakeStep)"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    class="bet-builder-step-btn"
                    :aria-label="$t('betBuilder.decreaseStake')"
                    @click="adjustStake(-stakeStep)"
                  >
                    −
                  </button>
                </div>
              </div>
            </label>
          </div>

          <p v-if="error" class="bet-builder-error">
            {{ error }}
          </p>

          <button
            type="button"
            class="bet-builder-primary bet-builder-primary--full"
            :disabled="!canBuild"
            @click="runMatch"
          >
            {{ $t('betBuilder.build') }}
          </button>
        </div>

        <!-- Scanning -->
        <div v-else-if="phase === 'scanning'" class="bet-builder-scan">
          <div class="bet-builder-scan-row">
            <span>{{ $t('betBuilder.scanning') }}</span>
            <span>{{ scanProgress }}%</span>
          </div>
          <div class="bet-builder-progress" role="progressbar" :aria-valuenow="scanProgress" aria-valuemin="0" aria-valuemax="100">
            <div class="bet-builder-progress-fill" :style="{ width: `${scanProgress}%` }" />
          </div>
          <p class="bet-builder-scan-note">
            {{ $t('betBuilder.scanNote') }}
          </p>
        </div>

        <!-- Result -->
        <div v-else-if="phase === 'result' && match" class="bet-builder-result">
          <div class="bet-builder-result-top">
            <span class="bet-builder-result-label">{{ $t('betBuilder.bestMatch') }}</span>
            <span class="bet-builder-result-payout">{{ formatMoney(match.payout) }}</span>
          </div>

          <div class="bet-builder-result-draw">
            <strong>{{ match.game_name }}</strong>
            <span>{{ formatDrawTime(match.draw.draw_date) }}</span>
          </div>

          <p class="bet-builder-result-market">
            {{ formatMarketLabel(match.pick, match.odds) }}
          </p>

          <div v-if="displayNumbers.length" class="bet-builder-numbers">
            <span
              v-for="(num, index) in displayNumbers"
              :key="`${num}-${index}`"
              class="number-ball"
              :class="{ bonus: index >= (match.numbers[0]?.length ?? 0) }"
            >{{ num }}</span>
          </div>

          <div class="bet-builder-result-meta">
            <span>{{ $t('betBuilder.stake') }}: {{ formatMoney(match.stake) }}</span>
            <span>{{ $t('betBuilder.targetWin') }}: {{ formatMoney(match.target_win) }}</span>
          </div>

          <div class="bet-builder-actions">
            <button type="button" class="bet-builder-secondary" @click="betBuilderStore.returnToConfig()">
              {{ $t('betBuilder.buildAnother') }}
            </button>
            <button type="button" class="bet-builder-primary" @click="confirmBet(false)">
              {{ $t('betBuilder.addToBetslip') }}
            </button>
          </div>
          <button type="button" class="bet-builder-tertiary" @click="confirmBet(true)">
            {{ $t('betBuilder.addAndContinue') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBetBuilderStore } from '@/stores/betBuilder'
import { useIntegrationMode } from '@/composables/useIntegrationMode'
import { useGameStore } from '@/stores/game'
import { useBetslipStore } from '@/stores/betslip'
import { useToast } from '@/composables/useToast'
import { formatMarketLabel, matchToBetslipItem } from '@/types/betBuilder'
import { iframeCommunication } from '@/utils/iframeCommunication'

const { t } = useI18n()
const betBuilderStore = useBetBuilderStore()
const gameStore = useGameStore()
const betslipStore = useBetslipStore()
const { showToast } = useToast()
const { isLite } = useIntegrationMode()

const localTargetWin = ref(betBuilderStore.targetWin)
const localStake = ref(betBuilderStore.stake)

const isEnabled = computed(() =>
  !isLite.value && betBuilderStore.isModuleEnabled(gameStore.getBetSettings?.enabled_modules)
)

const phase = computed(() => betBuilderStore.phase)
const match = computed(() => betBuilderStore.match)
const error = computed(() => betBuilderStore.error)
const scanProgress = computed(() => betBuilderStore.scanProgress)
const sessionAddedCount = computed(() => betBuilderStore.sessionAddedCount)

const betMin = computed(() => Number(gameStore.getBetSettings?.bet_min) || 1)
const betMax = computed(() => Number(gameStore.getBetSettings?.bet_max) || 100000)
const stakeStep = computed(() => (gameStore.getBetSettings?.allow_decimals ? 0.5 : 1))
const currencySymbol = computed(() => gameStore.getBetSettings?.currency_symbol || '')

const useOnlinePlatformBetslip = computed(() => {
  const ch = gameStore.getBetSettings?.client_surface_ui?.online
  return ch?.use_platform_betslip !== false
})

const canBuild = computed(() => localTargetWin.value > 0 && localStake.value >= betMin.value)

const displayNumbers = computed(() => {
  if (!match.value?.numbers?.length) return []
  const main = match.value.numbers[0] || []
  const bonus = match.value.numbers[1] || []
  return [...main, ...bonus]
})

watch(
  () => gameStore.getBetSettings?.bet_default,
  (defaultStake) => {
    if (typeof defaultStake === 'number' && defaultStake > 0 && betBuilderStore.phase === 'idle') {
      betBuilderStore.stake = defaultStake
      localStake.value = defaultStake
    }
  },
  { immediate: true }
)

function formatMoney(value: number): string {
  const symbol = currencySymbol.value
  const rounded = Math.round(value)
  return symbol ? `${symbol}${rounded.toLocaleString('en-US')}` : rounded.toLocaleString('en-US')
}

function formatDrawTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function openPanel() {
  betBuilderStore.openConfig(localStake.value, localTargetWin.value)
  localTargetWin.value = betBuilderStore.targetWin
  localStake.value = betBuilderStore.stake
}

function closePanel() {
  betBuilderStore.close()
}

function adjustTarget(delta: number) {
  localTargetWin.value = Math.max(1, Math.round((localTargetWin.value || 0) + delta))
}

function adjustStake(delta: number) {
  const next = Math.max(betMin.value, (localStake.value || 0) + delta)
  localStake.value = betMax.value > 0 ? Math.min(next, betMax.value) : next
}

async function runMatch() {
  betBuilderStore.targetWin = localTargetWin.value
  betBuilderStore.stake = localStake.value
  await betBuilderStore.findMatch()
}

function confirmBet(continueBuilding: boolean) {
  if (!match.value) return

  const betslipItem = matchToBetslipItem(match.value)

  if (!useOnlinePlatformBetslip.value) {
    iframeCommunication.sendMessage('bets-added', { bets: [betslipItem] })
    try {
      window.dispatchEvent(
        new CustomEvent('numbers-online-betslip-selection', { detail: { bets: [betslipItem] } })
      )
    } catch {
      /* ignore */
    }
    showToast({
      title: t('toast.selectionSent'),
      message: t('toast.externalIntegrationMsg'),
      type: 'info',
      duration: 4000,
    })
    if (continueBuilding) {
      betBuilderStore.recordAdded()
      betBuilderStore.returnToConfig()
    } else {
      betBuilderStore.close()
    }
    return
  }

  const added = betslipStore.addBet(betslipItem)
  if (!added) {
    showToast({
      title: t('toast.betAlreadyAdded'),
      message: t('toast.betAlreadyAddedMsg'),
      type: 'info',
      duration: 3000,
    })
    if (continueBuilding) {
      betBuilderStore.returnToConfig()
    }
    return
  }

  iframeCommunication.sendMessage('bets-added', { bets: [betslipItem] })
  betBuilderStore.recordAdded()
  showToast({
    title: t('betBuilder.addedTitle'),
    message: continueBuilding
      ? t('betBuilder.addedContinueMessage', { game: match.value.game_name })
      : t('betBuilder.addedMessage', { game: match.value.game_name }),
    type: 'success',
    duration: 3000,
  })

  if (continueBuilding) {
    betBuilderStore.returnToConfig()
  } else {
    betBuilderStore.close()
  }
}
</script>

<style scoped>
.bet-builder {
  margin-bottom: 24px;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
}

.bet-builder-card {
  box-sizing: border-box;
  border: 1px solid var(--colour-surface-cont);
  border-radius: var(--results-filter-radius, 8px);
  background: var(--colour-surface-cont-lowest);
  box-shadow: var(--shadow-control, none);
  overflow: hidden;
  color: var(--colour-base-text-colour);
}

.bet-builder-card--idle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 18px;
}

.bet-builder-intro {
  flex: 1;
  min-width: 0;
}

.bet-builder-eyebrow {
  display: inline-block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--colour-orange-text-colour, var(--colour-orange-button));
}

.bet-builder-heading {
  margin: 0 0 6px;
  font-size: var(--section-header-simple-title-size, 20px);
  font-weight: var(--section-header-simple-title-weight, 500);
  line-height: 1.25;
  color: var(--colour-base-text-colour);
}

.bet-builder-lead,
.bet-builder-body-lead {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--colour-secondary-text-colour);
}

.bet-builder-body-lead {
  margin-bottom: 16px;
}

.bet-builder-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: var(--section-header-simple-padding, 10px 16px);
  background: var(--section-header-simple-bg, var(--colour-surface-cont-low));
  color: var(--section-header-simple-text, var(--colour-text-inverse));
}

.bet-builder-bar-title {
  margin: 0;
  font-size: var(--section-header-simple-title-size, 20px);
  font-weight: var(--section-header-simple-title-weight, 500);
  line-height: 1.25;
  color: inherit;
}

.bet-builder-session-count {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--colour-secondary-text-on-dark, rgba(255, 255, 255, 0.72));
}

.bet-builder-dismiss {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: inherit;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
  opacity: 0.85;
}

.bet-builder-dismiss:hover {
  opacity: 1;
}

.bet-builder-body {
  padding: 16px 18px 18px;
}

.bet-builder-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.bet-builder-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.bet-builder-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--colour-secondary-text-colour);
}

.bet-builder-control {
  display: flex;
  align-items: stretch;
  height: 48px;
  box-sizing: border-box;
  border: 1px solid var(--colour-surface-cont);
  border-radius: var(--results-filter-radius, 8px);
  overflow: hidden;
  background: var(--colour-surface-cont-lowest);
}

.bet-builder-control:focus-within {
  border-color: var(--colour-surface-cont-high, var(--colour-surface-cont));
}

.bet-builder-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--colour-base-text-colour);
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  padding: 0 14px;
}

.bet-builder-step-buttons {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  border-left: 1px solid color-mix(in srgb, var(--colour-surface-cont) 88%, transparent);
}

.bet-builder-step-btn {
  flex: 1;
  min-height: 22px;
  border: none;
  padding: 0 12px;
  cursor: pointer;
  background: var(--colour-surface-cont-low);
  color: var(--text-control-strong, var(--colour-text-inverse));
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  font-family: inherit;
}

.bet-builder-step-btn:hover {
  filter: brightness(1.08);
}

.bet-builder-error {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--colour-red-text-colour);
}

.bet-builder-primary {
  border: none;
  border-radius: var(--betslip-primary-button-radius, 6px);
  height: 36px;
  padding: 0 16px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  background: var(--colour-green-button);
  color: var(--text-button, var(--colour-text-inverse));
  transition: background 0.2s;
}

.bet-builder-primary:hover:not(:disabled) {
  background: color-mix(in srgb, var(--colour-green-button) 88%, black 12%);
}

.bet-builder-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.bet-builder-primary--compact {
  flex-shrink: 0;
  white-space: nowrap;
}

.bet-builder-primary--full {
  width: 100%;
}

.bet-builder-secondary {
  height: 36px;
  padding: 0 12px;
  border-radius: var(--betslip-secondary-button-radius, 6px);
  border: 1px solid var(--colour-surface-cont);
  background: var(--colour-surface-cont-lowest);
  color: var(--bg-secondary-button-color, var(--colour-base-text-colour));
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.bet-builder-secondary:hover {
  background: var(--bg-hover, var(--colour-surface));
  border-color: var(--colour-orange-button);
}

.bet-builder-tertiary {
  width: 100%;
  margin-top: 10px;
  height: 36px;
  border-radius: var(--betslip-secondary-button-radius, 6px);
  border: 1px solid var(--colour-surface-cont);
  background: var(--colour-surface);
  color: var(--colour-base-text-colour);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.bet-builder-tertiary:hover {
  background: var(--colour-surface-cont-lowest);
}

.bet-builder-scan-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--colour-base-text-colour);
}

.bet-builder-progress {
  height: 6px;
  border-radius: 999px;
  background: var(--colour-surface-cont);
  overflow: hidden;
  margin-bottom: 10px;
}

.bet-builder-progress-fill {
  height: 100%;
  background: var(--colour-green-button);
  transition: width 0.15s ease;
}

.bet-builder-scan-note {
  margin: 0;
  font-size: 13px;
  color: var(--colour-secondary-text-colour);
}

.bet-builder-result-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 12px;
}

.bet-builder-result-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--colour-green-text-colour, var(--colour-green-button));
}

.bet-builder-result-payout {
  font-size: 20px;
  font-weight: 700;
  color: var(--colour-base-text-colour);
}

.bet-builder-result-draw {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}

.bet-builder-result-draw strong {
  font-size: 15px;
  font-weight: 600;
}

.bet-builder-result-draw span {
  font-size: 13px;
  color: var(--colour-secondary-text-colour);
}

.bet-builder-result-market {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.bet-builder-numbers {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.bet-builder-result-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  font-size: 13px;
  color: var(--colour-secondary-text-colour);
}

.bet-builder-actions {
  display: grid;
  grid-template-columns: 1fr 1.35fr;
  gap: 10px;
}

@media (max-width: 768px) {
  .bet-builder-card--idle {
    flex-direction: column;
    align-items: stretch;
  }

  .bet-builder-primary--compact {
    width: 100%;
  }

  .bet-builder-fields {
    grid-template-columns: 1fr;
  }

  .bet-builder-actions {
    grid-template-columns: 1fr;
  }
}
</style>
